import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, CircularProgress, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { toLocalIsoDate } from '../../../utils/dateUtils';
import { dayDiff, isLate, journeyPath, progressText, shippingThisWeek, shortDate } from './threadData';
import { useThreadOrders } from './useThreadData';

const whenText = (days: number) => (days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`);

interface Props {
    rows: IOrderDashboardRow[] | null;
    loading: boolean;
    error: boolean;
}

/** Orders shipping in the next 7 days (today included), soonest first. */
export function ShipThisWeekTable({ rows, loading, error }: Props) {
    const { hasPermission } = useAuth();
    const canOpen = hasPermission(PERMISSIONS.BUYER_ORDER_VIEW);
    const today = useMemo(() => toLocalIsoDate(new Date()), []);
    const due = useMemo(() => (rows ? shippingThisWeek(rows, today) : []), [rows, today]);

    if (loading && rows === null) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} aria-label="Loading shipments" /></Box>;
    }
    if (error) return <Alert severity="warning">Could not load the shipments.</Alert>;
    if (rows === null) return null;
    if (due.length === 0) return <Typography variant="body2" color="text.secondary">Nothing ships in the next 7 days.</Typography>;

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" data-testid="ship-this-week">
                <TableHead>
                    <TableRow>
                        <TableCell>Order</TableCell>
                        <TableCell>Buyer</TableCell>
                        <TableCell>Ships</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Progress</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {due.map(r => (
                        <TableRow key={r.orderId} hover>
                            <TableCell sx={{ fontWeight: 700 }}>
                                {canOpen
                                    ? <Link component={RouterLink} to={journeyPath(r.orderId)} data-help="dashboard.thread.shipRow" underline="hover">{r.orderNumber}</Link>
                                    : <span data-help="dashboard.thread.shipRow">{r.orderNumber}</span>}
                            </TableCell>
                            <TableCell>{r.buyerName}</TableCell>
                            <TableCell>{shortDate(r.shipDate)} · {whenText(dayDiff(today, r.shipDate))}</TableCell>
                            <TableCell align="right">{r.totalQuantity != null ? r.totalQuantity.toLocaleString('en-US') : '—'}</TableCell>
                            <TableCell sx={{ color: isLate(r) ? 'error.main' : 'inherit', fontWeight: isLate(r) ? 700 : 400 }}>{progressText(r)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}

/** Registry widget body (merchandising.shipThisWeek): loads its own rows. */
export default function ShipThisWeekWidget({ institutionId }: { institutionId: string }) {
    const { hasPermission } = useAuth();
    const orders = useThreadOrders(institutionId, hasPermission(PERMISSIONS.MERCH_REPORT_VIEW));
    return <ShipThisWeekTable rows={orders.rows} loading={orders.loading} error={orders.error} />;
}
